import { useState, useEffect, useRef } from "react";
import { format, isSameDay, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { Row, Col, Card, CardTitle, CardBody } from "reactstrap";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import APLPresenceCheckTable from "./APLPresenceCheckTable";

export default function APLPresenceWeeksView({
  attendanceByWeek,
  studentId,
  objId,
  level,
  activePeriodId,
}) {
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [weekIntervals, setWeekIntervals] = useState([[]]);

  const today = useRef(new Date());
  const numberOfWeeksToShow = useRef(4);
  const dateFormatDayName = "EEE";
  const dateFormatDayNumber = "d";
  const dateFormatDayNumberOfWeek = "i";
  const dateFormatWeekNumber = "I";
  const dateFormatYear = "yyyy";

  useEffect(() => {
    const tempWeekIntervals = [];
    const currentWeekNumber = format(today.current, dateFormatWeekNumber, {
      locale: sv,
    });
    const currentYear = format(today.current, dateFormatYear, {
      locale: sv,
    });

    for (
      let i = 0;
      i < attendanceByWeek.data.weeks.length;
      i += numberOfWeeksToShow.current
    ) {
      const chunk = attendanceByWeek.data.weeks.slice(
        i,
        i + numberOfWeeksToShow.current
      );
      tempWeekIntervals.push(chunk);
    }
    let currentWeekChunk = 0;
    tempWeekIntervals.forEach((interval, i) => {
      if (
        interval.some(
          (weeks) =>
            weeks.weekNumber === Number(currentWeekNumber) &&
            weeks.year === Number(currentYear)
        )
      ) {
        currentWeekChunk = i;
      }
    });
    setWeekIntervals(tempWeekIntervals);
    setCurrentWeekIndex(currentWeekChunk);
  }, [activePeriodId, today, attendanceByWeek.data.weeks]);

  const renderHeader = () => {
    const dateFormat = "MMM yyyy";
    const startOfCurrentTimeSpan = parseISO(
      weekIntervals[currentWeekIndex][0]?.attendanceForStudents[0]?.day
    );
    const endOfCurrentTimeSpan = parseISO(
      weekIntervals[currentWeekIndex]?.at(-1)?.attendanceForStudents?.at(-1).day
    );

    if (weekIntervals[currentWeekIndex][0]) {
      return (
        <Row className="flex-middle mb-2" style={{ marginLeft: "-19px" }}>
          <Col className="col ps-1 lead">
            <span>
              {format(startOfCurrentTimeSpan, dateFormat, { locale: sv })} -{" "}
              {format(endOfCurrentTimeSpan, dateFormat, { locale: sv })}
            </span>
          </Col>
        </Row>
      );
    }
  };

  const renderDaysAndCells = () => {
    const cells = [];
    const statusClassMap = {
      HadNoRegistrations: "no-registrations",
      WasPresent: "present",
      HadAbsence: "absent",
    };

    const attendanceData = weekIntervals[currentWeekIndex]?.flatMap((week) =>
      week.attendanceForStudents.filter(
        (record) => record.studentId === studentId
      )
    );

    attendanceData?.forEach((item) => {
      const formattedDayName = format(parseISO(item.day), dateFormatDayName, {
        locale: sv,
      });
      const formattedDayNumber = format(
        parseISO(item.day),
        dateFormatDayNumber,
        {
          locale: sv,
        }
      );
      const formattedDayNumberOfWeek = format(
        parseISO(item.day),
        dateFormatDayNumberOfWeek,
        {
          locale: sv,
        }
      );
      const formattedWeekNumber = format(
        parseISO(item.day),
        dateFormatWeekNumber,
        {
          locale: sv,
        }
      );

      const statusClass = item ? statusClassMap[item.status] || "" : "no-data";
      if (
        item &&
        formattedDayNumberOfWeek < 6 &&
        parseISO(item.day) <= today.current
      ) {
        cells.push(
          <div
            className="col calendar_day_container px-0"
            key={studentId + "" + item.day}
          >
            {formattedDayNumberOfWeek === "1" && (
              <div
                style={{
                  position: "absolute",
                  top: "2em",
                  width: "32px",
                  fontWeight: "bold",
                }}
              >
                v. {formattedWeekNumber}
              </div>
            )}
            <div
              className={
                formattedDayNumberOfWeek === "1"
                  ? "col calendar_dayname calendar_dayname_bold"
                  : "col calendar_dayname"
              }
            >
              {formattedDayName}
            </div>
            <div
              className={`col cell ${
                isSameDay(parseISO(item.day), today.current) ? "today" : ""
              } ${
                isSameDay(parseISO(item.day), selectedDate) ? "selected" : ""
              } ${statusClass}`}
              onClick={() => onDateClickHandle(parseISO(item.day), item)}
            >
              <span className="number">{formattedDayNumber}</span>
              <span className="bg" style={{ width: "100%" }}>
                {formattedDayNumber}
              </span>
            </div>
          </div>
        );
      }
    });

    return (
      <>
        <div
          className="body d-flex calendar_days_row"
          style={{ overflowX: "auto", paddingTop: ".8em" }}
        >
          {currentWeekIndex > 0 ? (
            <div
              className="calendar_change_dateInterval_chevron calendar_change_dateInterval_chevron_left p-1 m-2"
              style={{
                alignSelf: "flex-end",
                zIndex: 2,
              }}
              onClick={() => changeWeekHandle("prev")}
            >
              <FontAwesomeIcon
                icon="chevron-left"
                style={{ marginRight: "0.1rem" }}
              />
            </div>
          ) : (
            <span style={{ width: "18px" }}></span>
          )}
          <>{cells}</>

          {currentWeekIndex + 1 < weekIntervals.length && (
            <div
              className="calendar_change_dateInterval_chevron calendar_change_dateInterval_chevron_right p-1 m-2"
              style={{ alignSelf: "flex-end", zIndex: 2 }}
              onClick={() => changeWeekHandle("next")}
            >
              <FontAwesomeIcon
                icon="chevron-right"
                style={{ marginRight: "0.1rem" }}
              />
            </div>
          )}
        </div>
      </>
    );
  };

  const onDateClickHandle = (day, attendanceRecord) => {
    setSelectedDate(day);
    setSelectedDayData(attendanceRecord);
  };

  const changeWeekHandle = (btnType) => {
    if (btnType === "prev") {
      setCurrentWeekIndex(currentWeekIndex - 1);
    } else if (btnType === "next") {
      setCurrentWeekIndex(currentWeekIndex + 1);
    }
  };

  const renderDetails = () => {
    if (selectedDayData?.day) {
      return (
        <Col lg="6">
          <Card className="mt-3" style={{ marginLeft: "-30px" }}>
            <CardTitle className="p-2 mb-0 d-flex justify-content-between">
              {moment(selectedDayData?.day).format("Do MMMM YYYY")}
              <span
                className="float-end px-2 -mt-2"
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedDayData(null)}
              >
                x
              </span>
            </CardTitle>
            <CardBody>
              <APLPresenceCheckTable
                presenceData={selectedDayData}
                absence={selectedDayData.absencesThisDay}
                periodId={activePeriodId}
                studentId={studentId}
                objId={objId}
                level={level}
              />
            </CardBody>
          </Card>
        </Col>
      );
    } else {
      return null;
    }
  };
  if (weekIntervals.length >= 1) {
    return (
      <div className="calendar px-4">
        {renderHeader()}
        {renderDaysAndCells()}
        {selectedDayData !== null && renderDetails()}
      </div>
    );
  } else {
    return null;
  }
}
